import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button } from "@mui/material";
import CirculerLoader from "../../components/Loader";
import { isStrongPassword } from "../../utils/validators";

const ChangePassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resetPassWord, setResetPassWord] = useState("");
  const [confirmPassWord, setConfirmPassWord] = useState("");
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const baseURL = process.env.REACT_APP_BASE_URL;

  const validate = () => {
    let hasError = false;
    const trimmedPassword = resetPassWord.trim();
    const trimmedConfirm = confirmPassWord.trim();

    if (!isStrongPassword(trimmedPassword)) {
      setPasswordError(
        "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character."
      );
      hasError = true;
    } else {
      setPasswordError("");
    }

    if (trimmedPassword !== trimmedConfirm) {
      setConfirmPasswordError("Passwords do not match");
      hasError = true;
    } else {
      setConfirmPasswordError("");
    }

    return !hasError;
  };

  const ChangePassword = async (e: any) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      const response = await axios.post(
        `${baseURL}/user/forgotResetPassword`,
        {
          unique_id: id,
          newPassword: resetPassWord.trim(),
          confirmPassword: confirmPassWord.trim()
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.code !== 400) {
        setShowMessage(true);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error resetting password:", error);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const trimmedValue = value.trim();
    setResetPassWord(value);

    if (!isStrongPassword(trimmedValue)) {
      setPasswordError(
        "Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character."
      );
    } else {
      setPasswordError("");
    }

    if (trimmedValue !== confirmPassWord.trim()) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassWord(value);

    if (value.trim() !== resetPassWord.trim()) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError("");
    }
  };

  return (
    <>
      {showMessage ? (
        <div className="reset-password">
          <div className="reset-password-inner">
            <h1>Password Changed!</h1>
            <p>Your password has been changed successfully.</p>
            <div>
              <Link to="/school">Back to login</Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="reset-password">
          <div className="reset-password-inner">
            <form onSubmit={ChangePassword}>
              <h1>Reset your password</h1>
              <p className="change-pass-para">
                Enter a new password below to change your password.
              </p>

              {/* New password input */}
              <div className="input-container new-password-inner">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={resetPassWord}
                  onChange={handlePasswordChange}
                  placeholder=" "
                />
                <label>New password*</label>
                <span
                  className="eye-icon"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </span>
              </div>

              {/* Confirm password input */}
              <div className="input-container reenter-pass">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassWord}
                  onChange={handleConfirmPasswordChange}
                  placeholder=" "
                />
                <label>Re-enter new password*</label>
                <span
                  className="eye-icon"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </span>
              </div>

              {/* Validation messages */}
              {passwordError && (
                <p style={{ color: "red" }}>{passwordError}</p>
              )}
              {confirmPasswordError && (
                <p style={{ color: "red" }}>{confirmPasswordError}</p>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                variant="contained"
                className="continue-btn"
                sx={{ textTransform: "none", color: "#2C68A1", cursor: 'pointer' }}
              >
                {loading ? <CirculerLoader /> : "Reset password"}
              </Button>
            </form>
          </div>
        </div>
      )}

      <div className="footer-innner-reset">
        <p className="term-part">
          <a href="#">Terms of Use</a> | <a href="#">Privacy Policy</a>
        </p>
      </div>
    </>
  );
};

export default ChangePassword;
